import {
  useState,
  useEffect,
  useMemo,
  lazy,
  Suspense,
  type ComponentType,
  type LazyExoticComponent,
} from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { LayoutGroup, AnimatePresence, motion, MotionConfig } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { BackgroundSettings, DEFAULT_BG_SETTINGS, BG_SETTINGS_LS_KEY, BG_SETTINGS_EVENT } from './components/BackgroundControls'
import { ThemeProvider } from './contexts/ThemeContext'
import { AppReadyProvider, useAppReady } from './contexts/AppReadyContext'
import { AudioPlayerProvider } from './contexts/AudioPlayerContext'
// FloatingAudioPlayer removed - using AudioHeader instead for persistent controls
// import FloatingAudioPlayer from './components/constellation/FloatingAudioPlayer'
import { EphemeralTokenProvider } from './context/EphemeralTokenContext'
import UniversalChatProvider from './context/UniversalChatProvider'
import { AuthProvider } from './lib/auth/AuthContext'
import { ProtectedRoute } from './lib/auth/ProtectedRoute'
import ScrollToTop from './components/ScrollToTop'
import BrandModeRouter from './components/BrandModeRouter'
import SEOHead from './components/SEOHead'
import { CurrencyProvider } from './contexts/CurrencyContext'
import { HeaderPrefsProvider } from './contexts/HeaderPrefsContext'
import { LayoutProvider } from './contexts/LayoutProvider'
import { getSeoMetadata } from './lib/seo/routeMetadata'
import ErrorBoundary from './components/ui/ErrorBoundary'

const HomePage = lazy(() => import('./pages/HomePage'))
const HoldingPage = lazy(() => import('./pages/HoldingPage'))
const LoginPage = lazy(() => import('./pages/LoginPage'))
const AccessPendingPage = lazy(() => import('./pages/AccessPendingPage'))

const LivingOceanBackground = lazy(
  () => import('./components/LivingOceanBackground')
)
const UniverseBackground = lazy(() => import('./components/UniverseBackground'))
const LanguageExperience = lazy(
  () => import('./components/language-experience')
)
const AudioHeader = lazy(
  () => import('./components/constellation/premium/AudioHeader')
)
const AboutPage = lazy(() => import('./pages/AboutPage'))
const BrandPage = lazy(() => import('./pages/BrandPage'))
const TechnologyPage = lazy(() => import('./pages/TechnologyPage'))
const PricingPage = lazy(() => import('./pages/PricingPage'))
const UseCasesPage = lazy(() => import('./pages/UseCasesPage'))
const FAQPage = lazy(() => import('./pages/FAQPage'))
const SignupPage = lazy(() => import('./pages/SignupPage'))
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'))
const TermsPage = lazy(() => import('./pages/TermsPage'))
const ContactPage = lazy(() => import('./pages/ContactPage'))
const ContributePage = lazy(() => import('./pages/ContributePage'))
const RequestAccessPage = lazy(() => import('./pages/RequestAccessPage'))
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'))
const CareersPage = lazy(() => import('./pages/CareersPage'))
const ResearchPage = lazy(() => import('./pages/ResearchPage'))
const SafetyPage = lazy(() => import('./pages/SafetyPage'))
const SupportPage = lazy(() => import('./pages/SupportPage'))
const BlogPage = lazy(() => import('./pages/BlogPage'))
const BlogPostPage = lazy(() => import('./pages/blog/BlogPostPage'))
const PodcastPage = lazy(() => import('./pages/PodcastPage'))
const PodcastEpisodePage = lazy(() => import('./pages/podcast/PodcastEpisodePage'))
const CompliancePage = lazy(() => import('./pages/CompliancePage'))
const VisionPage = lazy(() => import('./pages/VisionPage'))
const MatrizPage = lazy(() => import('./pages/MatrizPage'))
const OrchestrationPage = lazy(() => import('./pages/OrchestrationPage'))
const VivoxPage = lazy(() => import('./pages/research/VivoxPage'))
const DreamModulePage = lazy(() => import('./pages/research/DreamModulePage'))
const GuardianPage = lazy(() => import('./pages/research/GuardianPage'))
const CognitiveMeshPage = lazy(() => import('./pages/research/CognitiveMeshPage'))
const MemoryHealixPage = lazy(() => import('./pages/research/MemoryHealixPage'))
const QuantumBioFusionPage = lazy(() => import('./pages/research/QuantumBioFusionPage'))
const SwarmConsciousnessPage = lazy(() => import('./pages/research/SwarmConsciousnessPage'))
const NIASPage = lazy(() => import('./pages/products/NIASPage'))
const DASTPage = lazy(() => import('./pages/products/DASTPage'))
const SEEDRAPage = lazy(() => import('./pages/products/SEEDRAPage'))
const ABASPage = lazy(() => import('./pages/products/ABASPage'))
const StarPage = lazy(() => import('./pages/constellation/StarPage'))
const ConstellationPage = lazy(() => import('./pages/ConstellationPage'))
const ConstellationOptionC = lazy(() => import('./pages/ConstellationOptionC'))
const TimelinePage = lazy(() => import('./pages/TimelinePage'))
const ExperiencePage = lazy(() => import('./pages/ExperiencePage'))
const ExperienceComingSoonPage = lazy(() => import('./pages/ExperienceComingSoonPage'))
const DreamExperiencePage = lazy(() => import('./pages/DreamExperiencePage'))
const PurpleExperiencePage = lazy(() => import('./pages/PurpleExperiencePage'))
const ShowcasePage = lazy(() => import('./pages/ShowcasePage'))
const TransparencyPage = lazy(() => import('./pages/TransparencyPage'))
const CapabilitiesReportPage = lazy(() => import('./pages/CapabilitiesReportPage'))
const EnterprisePage = lazy(() => import('./pages/EnterprisePage'))
const ChatFeaturePage = lazy(() => import('./pages/ChatPage'))
const PlaygroundPage = lazy(() => import('./pages/PlaygroundPage'))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'))
const ProofPage = lazy(() => import('./pages/ProofPage'))
const KnowledgePage = lazy(() => import('./pages/KnowledgePage'))
const AgentsPage = lazy(() => import('./pages/solutions/AgentsPage'))
const InsightsPage = lazy(() => import('./pages/solutions/InsightsPage'))
const CreativePage = lazy(() => import('./pages/solutions/CreativePage'))
const GovernancePage = lazy(() => import('./pages/solutions/GovernancePage'))
const HealthcarePage = lazy(() => import('./pages/solutions/HealthcarePage'))
const FinancePage = lazy(() => import('./pages/solutions/FinancePage'))
const EducationPage = lazy(() => import('./pages/solutions/EducationPage'))
const CreativeIndustriesPage = lazy(() => import('./pages/solutions/CreativeIndustriesPage'))
const InsurancePage = lazy(() => import('./pages/solutions/InsurancePage'))
const TransportPage = lazy(() => import('./pages/solutions/TransportPage'))
const FashionRetailPage = lazy(() => import('./pages/solutions/FashionRetailPage'))
const HospitalityPage = lazy(() => import('./pages/solutions/HospitalityPage'))
const PublicSectorPage = lazy(() => import('./pages/solutions/PublicSectorPage'))
const GroceryRetailPage = lazy(() => import('./pages/solutions/GroceryRetailPage'))
const GigLogisticsPage = lazy(() => import('./pages/solutions/GigLogisticsPage'))
const AviationPage = lazy(() => import('./pages/solutions/AviationPage'))
const QRGPage = lazy(() => import('./pages/QRGPage'))
const BackgroundShowcase = lazy(() => import('./pages/BackgroundShowcase'))
const ConstellationPreview = lazy(() => import('./pages/ConstellationPreview'))
const UniverseJourneyDemo = lazy(() => import('./pages/UniverseJourneyDemo'))
const EventsPage = lazy(() => import('./pages/EventsPage'))
const NewsletterPage = lazy(() => import('./pages/NewsletterPage'))
const CookieConsent = lazy(() => import('./components/CookieConsent'))
const AgenticChatBubble = lazy(() => import('./components/global/AgenticChatBubble'))
const DocsLayout = lazy(() => import('./components/docs/DocsLayout'))
const IntroductionContent = lazy(() => import('./pages/docs/IntroductionContent'))
const QuickstartContent = lazy(() => import('./pages/docs/QuickstartContent'))
const ApiContent = lazy(() => import('./pages/docs/ApiContent'))
const SdkContent = lazy(() => import('./pages/docs/SdkContent'))
const SdkPythonContent = lazy(() => import('./pages/docs/SdkPythonContent'))
const PlaceholderContent = lazy(() => import('./pages/docs/PlaceholderContent'))
const AuthenticationContent = lazy(() => import('./pages/docs/AuthenticationContent'))
const RateLimitsContent = lazy(() => import('./pages/docs/RateLimitsContent'))
const ApiDreamContent = lazy(() => import('./pages/docs/ApiDreamContent'))
const ApiMatrizContent = lazy(() => import('./pages/docs/ApiMatrizContent'))
const BioInspiredContent = lazy(() => import('./pages/docs/BioInspiredContent'))
const CliContent = lazy(() => import('./pages/docs/CliContent'))
const InstallationContent = lazy(() => import('./pages/docs/InstallationContent'))
const McpContent = lazy(() => import('./pages/docs/McpContent'))
const PluginsContent = lazy(() => import('./pages/docs/PluginsContent'))
const QuantumInspiredContent = lazy(() => import('./pages/docs/QuantumInspiredContent'))
const ApiConsciousnessContent = lazy(() => import('./pages/docs/ApiConsciousnessContent'))
const ApiMemoryContent = lazy(() => import('./pages/docs/ApiMemoryContent'))
const ApiOrchestrationContent = lazy(() => import('./pages/docs/ApiOrchestrationContent'))
const ApiGuardianContent = lazy(() => import('./pages/docs/ApiGuardianContent'))
const SdkJavascriptContent = lazy(() => import('./pages/docs/SdkJavascriptContent'))
const SdkGoContent = lazy(() => import('./pages/docs/SdkGoContent'))
const SdkJavaContent = lazy(() => import('./pages/docs/SdkJavaContent'))
const ConstellationContent = lazy(() => import('./pages/docs/ConstellationContent'))
const ConsciousnessConceptContent = lazy(() => import('./pages/docs/ConsciousnessConceptContent'))
const MemoryConceptContent = lazy(() => import('./pages/docs/MemoryConceptContent'))
const DreamConceptContent = lazy(() => import('./pages/docs/DreamConceptContent'))
const MatrizConceptContent = lazy(() => import('./pages/docs/MatrizConceptContent'))
const TutorialsContent = lazy(() => import('./pages/docs/TutorialsContent'))
const DatasetOnboardingTutorial = lazy(() => import('./pages/docs/tutorials/DatasetOnboardingTutorial'))
const GuardianPlanningTutorial = lazy(() => import('./pages/docs/tutorials/GuardianPlanningTutorial'))
const DreamExplorationTutorial = lazy(() => import('./pages/docs/tutorials/DreamExplorationTutorial'))
const BestPracticesContent = lazy(() => import('./pages/docs/BestPracticesContent'))
const ExamplesContent = lazy(() => import('./pages/docs/ExamplesContent'))
const UseCasesContent = lazy(() => import('./pages/docs/UseCasesContent'))
const ChangelogContent = lazy(() => import('./pages/docs/ChangelogContent'))
const ErrorsContent = lazy(() => import('./pages/docs/ErrorsContent'))
const StudioLayout = lazy(() => import('./components/studio/StudioLayout'))
const StudioLandingPage = lazy(() => import('./pages/StudioLandingPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const SSOCallbackPage = lazy(() => import('./pages/sso/SSOCallbackPage'))
const ChatPage = lazy(() => import('./app/studio/chat/page'))
const SecretsPage = lazy(() => import('./app/studio/secrets/page'))
const StudioSettingsPage = lazy(() => import('./app/studio/settings/page'))
const ApiKeysPage = lazy(() => import('./app/studio/keys/page'))
const BillingPage = lazy(() => import('./app/studio/billing/page'))
const UsagePage = lazy(() => import('./app/studio/usage/page'))
const UpgradePage = lazy(() => import('./app/studio/upgrade/page'))
const SecurityPage = lazy(() => import('./app/studio/security/page'))
const PreferencesPage = lazy(() => import('./app/studio/preferences/page'))
const ConnectorsPage = lazy(() => import('./app/studio/connectors/page'))
const ConnectorCallbackPage = lazy(() => import('./app/studio/connectors/callback/page'))
const QRGPlaygroundPage = lazy(() => import('./app/studio/qrg-playground/page'))
const GatedEntry = lazy(() => import('./components/global/GatedEntry'))
const PortalPage = lazy(() => import('./pages/PortalPage'))
const BetaGatePage = lazy(() => import('./pages/beta/BetaGatePage'))
const BetaAcknowledgePage = lazy(() => import('./pages/beta/BetaAcknowledgePage'))
const BetaWorkspacePage = lazy(() => import('./pages/beta/BetaWorkspacePage'))
const BetaBlockedPage = lazy(() => import('./pages/beta/BetaBlockedPage'))

// Language gate key - sync with languageRegions.ts SELECTION_COMPLETE_KEY
const LANGUAGE_SELECTED_KEY = 'lukhas_language_selected'

// Language gate bypass (for development / experimentation only).
// Controlled via env so it cannot be accidentally shipped enabled.
// Safe default: OFF. Hard-disabled in production builds via NODE_ENV guard.
const IS_PRODUCTION =
  typeof process !== 'undefined' &&
  typeof process.env !== 'undefined' &&
  process.env.NODE_ENV === 'production'

const BYPASS_LANGUAGE_GATE =
  !IS_PRODUCTION &&
  typeof process !== 'undefined' &&
  typeof process.env !== 'undefined' &&
  (process.env.REACT_APP_BYPASS_LANGUAGE_GATE === 'true' ||
    process.env.VITE_BYPASS_LANGUAGE_GATE === 'true')

if (
  typeof process !== 'undefined' &&
  typeof process.env !== 'undefined' &&
  process.env.NODE_ENV === 'production' &&
  (process.env.REACT_APP_BYPASS_LANGUAGE_GATE === 'true' ||
    process.env.VITE_BYPASS_LANGUAGE_GATE === 'true')
) {
  throw new Error(
    'Language gate bypass must not be enabled in production builds. ' +
      'Unset REACT_APP_BYPASS_LANGUAGE_GATE/VITE_BYPASS_LANGUAGE_GATE or rebuild.'
  )
}

if (BYPASS_LANGUAGE_GATE) {
  console.warn(
    'Language gate bypass is ENABLED via environment flag. ' +
      'Ensure this remains disabled in production builds.'
  )
}

function renderLazy(
  Component: LazyExoticComponent<ComponentType<Record<string, never>>>
) {
  return (
    <Suspense fallback={null}>
      <Component />
    </Suspense>
  )
}

function renderLazyWithProps<P extends object>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Component: LazyExoticComponent<ComponentType<any>>,
  props: P
) {
  const C = Component as ComponentType<P>
  return (
    <Suspense fallback={null}>
      <C {...props} />
    </Suspense>
  )
}

// Main content component that consumes AppReady context for transitions
function AppContent({ languageSelected }: { languageSelected: boolean }) {
  const { transitionPhase, setTransitionPhase } = useAppReady()
  const location = useLocation()
  const { i18n } = useTranslation()
  const seoMetadata = getSeoMetadata(location.pathname)

  // Check if we're on a constellation route (pill AudioHeader only)
  const isConstellationRoute = useMemo(() => {
    return location.pathname.startsWith('/constellation')
  }, [location.pathname])

  // About page gets a distinct violet/identity ocean variant
  const isAboutRoute = location.pathname === '/about'
  const isAviationRoute = location.pathname === '/solutions/aviation'

  // Background settings — read from localStorage on mount, updated via custom event from /settings
  const [bgSettings, setBgSettings] = useState<BackgroundSettings>(() => {
    try {
      const saved = localStorage.getItem(BG_SETTINGS_LS_KEY)
      if (saved) return { ...DEFAULT_BG_SETTINGS, ...JSON.parse(saved) }
    } catch { /* ignore */ }
    return DEFAULT_BG_SETTINGS
  })

  // Sync <html lang> attribute with active i18n language for screen readers
  useEffect(() => {
    document.documentElement.lang = i18n.language
  }, [i18n.language])

  // Re-read from localStorage when any settings page broadcasts a change
  useEffect(() => {
    const handler = () => {
      try {
        const saved = localStorage.getItem(BG_SETTINGS_LS_KEY)
        if (saved) setBgSettings({ ...DEFAULT_BG_SETTINGS, ...JSON.parse(saved) })
      } catch { /* ignore */ }
    }
    window.addEventListener(BG_SETTINGS_EVENT, handler)
    return () => window.removeEventListener(BG_SETTINGS_EVENT, handler)
  }, [])

  // Sync <html lang> with active i18n language for screen-reader correctness
  useEffect(() => {
    document.documentElement.lang = i18n.language
  }, [i18n.language])

  // If language is already selected (from localStorage), skip to complete phase
  useEffect(() => {
    if (languageSelected && transitionPhase === 'language') {
      setTransitionPhase('complete')
    }
  }, [languageSelected, transitionPhase, setTransitionPhase])

  // Determine if main content should be visible:
  // Only show after both language AND cookie gates are complete.
  // During 'language', 'dissolving', and 'cookies' phases, keep main content hidden
  // so the ocean background shows through behind the gate overlays.
  const showMainContent =
    transitionPhase === 'hero' || transitionPhase === 'complete'

  return (
    <MotionConfig reducedMotion="user">
    <BrandModeRouter>
    <LayoutGroup>
      <SEOHead
        description={seoMetadata.description}
        title={seoMetadata.title}
        type={seoMetadata.type}
        url={seoMetadata.url}
      />
      {/* Scroll to top on route changes */}
      <ScrollToTop />

      {/* SINGLE persistent background - visible throughout ALL phases */}
      {/* Base dark background at z-[-20] */}
      <div
        className="fixed inset-0 z-[-20]"
        style={{ background: 'rgb(2, 6, 15)' }}
      />
      {/* Two-layer background: softened ocean behind, globe in front — disabled on Aviation (own globe) */}
      {!isAviationRoute && renderLazyWithProps(LivingOceanBackground, {
        opacity: bgSettings.oceanOpacity,
        zIndex: -8,
        variant: isAboutRoute ? 'about' : 'default',
        ringIntensity: bgSettings.oceanRingIntensity,
      })}
      {!isAviationRoute && renderLazyWithProps(UniverseBackground, {
        languageSelected,
        pathname: location.pathname,
        overrides: {
          opacityMultiplier: bgSettings.globeOpacity,
          scaleMultiplier: bgSettings.globeScale,
          brightnessMultiplier: bgSettings.globeBrightness,
          rotationSpeed: bgSettings.globeRotationSpeed,
        },
      })}

      <div className="min-h-screen relative z-10">
        {/* Routes container - smooth opacity transition based on phase */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{
            opacity: showMainContent ? 1 : 0,
          }}
          transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
          style={{
            pointerEvents: showMainContent ? 'auto' : 'none',
            height: languageSelected ? 'auto' : '100vh',
            overflow: languageSelected ? 'visible' : 'hidden',
          }}
        >
              {/* ErrorBoundary catches rendering errors to prevent white screen */}
              <ErrorBoundary>
              {/* AnimatePresence enables exit animations when routes change */}
              <AnimatePresence mode="wait">
              <Routes location={location} key={location.pathname}>
                {/* Public pages */}
                <Route path="/" element={renderLazy(HomePage)} />
                <Route path="/login" element={renderLazy(LoginPage)} />
                <Route path="/access-pending" element={renderLazy(AccessPendingPage)} />
                <Route path="/about" element={renderLazy(AboutPage)} />
                <Route path="/brand" element={renderLazy(BrandPage)} />
                {/* Developer Playground - AI model testing interface */}
                <Route path="/playground" element={renderLazy(PlaygroundPage)} />
                <Route path="/technology" element={renderLazy(TechnologyPage)} />
                <Route path="/pricing" element={renderLazy(PricingPage)} />
                <Route path="/signup" element={renderLazy(SignupPage)} />
                <Route path="/use-cases" element={renderLazy(UseCasesPage)} />
                <Route path="/faq" element={renderLazy(FAQPage)} />
                <Route path="/privacy" element={renderLazy(PrivacyPage)} />
                <Route path="/terms" element={renderLazy(TermsPage)} />
                <Route path="/contact" element={renderLazy(ContactPage)} />
                <Route path="/contribute" element={renderLazy(ContributePage)} />
                <Route path="/request-access" element={renderLazy(RequestAccessPage)} />
                <Route path="/cookies" element={renderLazy(CookiePolicyPage)} />
                <Route path="/compliance" element={renderLazy(CompliancePage)} />
                <Route path="/careers" element={renderLazy(CareersPage)} />
                <Route path="/careers/apply" element={renderLazy(CareersPage)} />
                <Route path="/research" element={renderLazy(ResearchPage)} />
                <Route path="/safety" element={renderLazy(SafetyPage)} />
                <Route path="/support" element={renderLazy(SupportPage)} />
                <Route path="/proof" element={renderLazy(ProofPage)} />
                <Route path="/knowledge" element={renderLazy(KnowledgePage)} />
                <Route path="/blog" element={renderLazy(BlogPage)} />
                <Route path="/blog/:slug" element={renderLazy(BlogPostPage)} />
                <Route path="/podcast" element={renderLazy(PodcastPage)} />
                <Route path="/podcast/:slug" element={renderLazy(PodcastEpisodePage)} />
                <Route path="/vision" element={renderLazy(VisionPage)} />
                <Route path="/matriz" element={renderLazy(MatrizPage)} />
                <Route path="/orchestration" element={renderLazy(OrchestrationPage)} />
                <Route path="/research/vivox" element={renderLazy(VivoxPage)} />
                <Route path="/research/dream-module" element={renderLazy(DreamModulePage)} />
                <Route path="/research/guardian" element={renderLazy(GuardianPage)} />
                <Route path="/research/cognitive-mesh" element={renderLazy(CognitiveMeshPage)} />
                <Route path="/research/memory-healix" element={renderLazy(MemoryHealixPage)} />
                <Route path="/research/quantum-bio-fusion" element={renderLazy(QuantumBioFusionPage)} />
                <Route path="/research/swarm-consciousness" element={renderLazy(SwarmConsciousnessPage)} />

                {/* Product pages */}
                <Route path="/products/nias" element={renderLazy(NIASPage)} />
                <Route path="/products/dast" element={renderLazy(DASTPage)} />
                <Route path="/products/seedra" element={renderLazy(SEEDRAPage)} />
                <Route path="/products/abas" element={renderLazy(ABASPage)} />

                {/* Constellation experience with CONSTELLΛTION branding */}
                <Route path="/constellation" element={renderLazy(ConstellationPage)} />
                <Route path="/constellation/option-c" element={renderLazy(ConstellationOptionC)} />
                <Route path="/constellation/:slug" element={renderLazy(StarPage)} />
                <Route path="/timeline" element={renderLazy(TimelinePage)} />
                <Route path="/experience/coming-soon" element={renderLazy(ExperienceComingSoonPage)} />
                <Route path="/experience" element={renderLazy(ExperiencePage)} />
                <Route path="/dream-experience" element={renderLazy(DreamExperiencePage)} />
                <Route path="/purple-experience" element={renderLazy(PurpleExperiencePage)} />
                <Route path="/showcase" element={renderLazy(ShowcasePage)} />
                <Route path="/transparency" element={renderLazy(TransparencyPage)} />
                <Route path="/capabilities" element={renderLazy(CapabilitiesReportPage)} />

                {/* Solutions - Capability Pillars */}
                <Route path="/solutions/agents" element={renderLazy(AgentsPage)} />
                <Route path="/solutions/insights" element={renderLazy(InsightsPage)} />
                <Route path="/solutions/creative" element={renderLazy(CreativePage)} />
                <Route path="/solutions/governance" element={renderLazy(GovernancePage)} />
                {/* Solutions - Industry Verticals */}
                <Route path="/solutions/healthcare" element={renderLazy(HealthcarePage)} />
                <Route path="/solutions/finance" element={renderLazy(FinancePage)} />
                <Route path="/solutions/education" element={renderLazy(EducationPage)} />
                <Route path="/solutions/creative-industries" element={renderLazy(CreativeIndustriesPage)} />
                <Route path="/solutions/insurance" element={renderLazy(InsurancePage)} />
                <Route path="/solutions/transport" element={renderLazy(TransportPage)} />
                <Route path="/solutions/fashion-retail" element={renderLazy(FashionRetailPage)} />
                <Route path="/solutions/hospitality" element={renderLazy(HospitalityPage)} />
                <Route path="/solutions/public-sector" element={renderLazy(PublicSectorPage)} />
                <Route path="/solutions/grocery-retail" element={renderLazy(GroceryRetailPage)} />
                <Route path="/solutions/gig-logistics" element={renderLazy(GigLogisticsPage)} />
                <Route path="/solutions/aviation" element={renderLazy(AviationPage)} />

                {/* Enterprise */}
                <Route path="/enterprise" element={renderLazy(EnterprisePage)} />

                {/* CHΛT! feature page */}
                <Route path="/chat" element={renderLazy(ChatFeaturePage)} />

                {/* Developer Documentation with shared layout */}
                <Route path="/docs" element={renderLazy(DocsLayout)}>
                  <Route index element={renderLazy(IntroductionContent)} />
                  <Route path="quickstart" element={renderLazy(QuickstartContent)} />
                  <Route path="installation" element={renderLazy(InstallationContent)} />
                  <Route path="authentication" element={renderLazy(AuthenticationContent)} />
                  <Route path="api" element={renderLazy(ApiContent)} />
                  <Route path="api/consciousness" element={renderLazy(ApiConsciousnessContent)} />
                  <Route path="api/memory" element={renderLazy(ApiMemoryContent)} />
                  <Route path="api/orchestration" element={renderLazy(ApiOrchestrationContent)} />
                  <Route path="api/guardian" element={renderLazy(ApiGuardianContent)} />
                  <Route path="sdk" element={renderLazy(SdkContent)} />
                  <Route path="sdk/python" element={renderLazy(SdkPythonContent)} />
                  <Route path="sdk/javascript" element={renderLazy(SdkJavascriptContent)} />
                  <Route path="sdk/go" element={renderLazy(SdkGoContent)} />
                  <Route path="sdk/java" element={renderLazy(SdkJavaContent)} />
                  <Route path="constellation" element={renderLazy(ConstellationContent)} />
                  <Route path="consciousness" element={renderLazy(ConsciousnessConceptContent)} />
                  <Route path="memory" element={renderLazy(MemoryConceptContent)} />
                  <Route path="dream" element={renderLazy(DreamConceptContent)} />
                  <Route path="matriz" element={renderLazy(MatrizConceptContent)} />
                  <Route path="tutorials" element={renderLazy(TutorialsContent)} />
                  <Route path="tutorials/dataset-onboarding" element={renderLazy(DatasetOnboardingTutorial)} />
                  <Route path="tutorials/guardian-planning" element={renderLazy(GuardianPlanningTutorial)} />
                  <Route path="tutorials/dream-exploration" element={renderLazy(DreamExplorationTutorial)} />
                  <Route path="best-practices" element={renderLazy(BestPracticesContent)} />
                  <Route path="examples" element={renderLazy(ExamplesContent)} />
                  <Route path="use-cases" element={renderLazy(UseCasesContent)} />
                  <Route path="changelog" element={renderLazy(ChangelogContent)} />
                  <Route path="rate-limits" element={renderLazy(RateLimitsContent)} />
                  <Route path="errors" element={renderLazy(ErrorsContent)} />
                  <Route path="quantum-inspired" element={renderLazy(QuantumInspiredContent)} />
                  <Route path="bio-inspired" element={renderLazy(BioInspiredContent)} />
                  <Route path="api/dream" element={renderLazy(ApiDreamContent)} />
                  <Route path="api/matriz" element={renderLazy(ApiMatrizContent)} />
                  <Route path="sdk/cli" element={renderLazy(CliContent)} />
                  <Route path="mcp" element={renderLazy(McpContent)} />
                  <Route path="plugins" element={renderLazy(PluginsContent)} />
                  <Route path="*" element={renderLazy(PlaceholderContent)} />
                </Route>

                {/* Community */}
                <Route path="/events" element={renderLazy(EventsPage)} />
                <Route path="/newsletter" element={renderLazy(NewsletterPage)} />

                {/* Developer Tools */}
                <Route path="/backgrounds" element={renderLazy(BackgroundShowcase)} />
                <Route path="/universe" element={renderLazy(UniverseJourneyDemo)} />
                <Route path="/constellation-preview" element={renderLazy(ConstellationPreview)} />

                {/* QRG — Quantum Resistant Glyph technical museum exhibit */}
                <Route
                  path="/qrg"
                  element={renderLazy(QRGPage)}
                />

                {/* SSO callback — receives JWT from lukhas.id after cross-domain auth */}
                <Route path="/sso/callback" element={renderLazy(SSOCallbackPage)} />

                {/* Public Settings - redirects to Studio settings if logged in */}
                <Route path="/settings" element={renderLazy(SettingsPage)} />

                {/* Console aliases — foyer CTAs use /console, actual workspace is /studio/app */}
                <Route path="/console" element={<Navigate to="/studio/app" replace />} />
                <Route path="/console/connectors" element={<Navigate to="/studio/app/connectors" replace />} />

                {/* Studio landing page (marketing intro) */}
                <Route path="/studio" element={renderLazy(StudioLandingPage)} />

                {/* Studio app routes with shared layout - requires authentication */}
                <Route path="/studio/app" element={
                  <ProtectedRoute>
                    {renderLazy(StudioLayout)}
                  </ProtectedRoute>
                }>
                  <Route index element={<Navigate to="/studio/app/chat" replace />} />
                  <Route path="chat" element={renderLazy(ChatPage)} />
                  <Route path="keys" element={renderLazy(ApiKeysPage)} />
                  <Route path="secrets" element={renderLazy(SecretsPage)} />
                  <Route path="settings" element={renderLazy(StudioSettingsPage)} />
                  <Route path="billing" element={renderLazy(BillingPage)} />
                  <Route path="usage" element={renderLazy(UsagePage)} />
                  <Route path="upgrade" element={renderLazy(UpgradePage)} />
                  <Route path="security" element={renderLazy(SecurityPage)} />
                  <Route path="security/passkeys" element={<Navigate to="/studio/app/security" replace />} />
                  <Route path="preferences" element={renderLazy(PreferencesPage)} />
                  <Route path="connectors" element={renderLazy(ConnectorsPage)} />
                  <Route path="connectors/callback" element={renderLazy(ConnectorCallbackPage)} />
                  <Route path="qrg-playground" element={renderLazy(QRGPlaygroundPage)} />
                </Route>

                {/* Beta onboarding — gate, acknowledge, workspace, blocked */}
                <Route path="/beta" element={renderLazy(BetaGatePage)} />
                <Route path="/beta/acknowledge" element={renderLazy(BetaAcknowledgePage)} />
                <Route path="/beta/workspace" element={
                  <ProtectedRoute>
                    {renderLazy(BetaWorkspacePage)}
                  </ProtectedRoute>
                } />
                <Route path="/beta/blocked" element={renderLazy(BetaBlockedPage)} />

                {/* Gated Portal — controlled access entry point */}
                <Route path="/portal" element={renderLazy(PortalPage)} />

                {/* 404 — show a helpful page instead of silently redirecting */}
                <Route path="*" element={renderLazy(NotFoundPage)} />
              </Routes>
              </AnimatePresence>
              </ErrorBoundary>
            </motion.div>
            {/* CookieConsent — non-blocking bottom banner (R50C-1) */}
            {renderLazy(CookieConsent)}
            {/* AgenticChatBubble - floating agentic assistant */}
            {languageSelected && renderLazy(AgenticChatBubble)}
            {/* AudioHeader pill only on constellation routes */}
            {languageSelected && isConstellationRoute && renderLazy(AudioHeader)}
            {/* LanguageExperience with AnimatePresence for proper exit animations */}
            <AnimatePresence mode="wait">
              {!languageSelected && (
                <Suspense fallback={null}>
                  <LanguageExperience key="language-experience" />
                </Suspense>
              )}
            </AnimatePresence>
          </div>
        </LayoutGroup>
    </BrandModeRouter>
    </MotionConfig>
  )
}

function GatedApplication() {
  // R50C-1: Language is always "selected" — auto-detected on first visit.
  // No blocking language modal. The LanguageExperience component handles
  // auto-detection silently and sets localStorage.
  const [languageSelected, setLanguageSelected] = useState(true)

  // Sync state when custom event is dispatched (after dissolve animation)
  useEffect(() => {
    const sync = () => {
      setLanguageSelected(localStorage.getItem(LANGUAGE_SELECTED_KEY) === 'true')
    }
    window.addEventListener('lukhas:language-selected', sync)
    return () => window.removeEventListener('lukhas:language-selected', sync)
  }, [])

  // Re-open language modal when LanguageIndicator is clicked
  useEffect(() => {
    const reopen = () => {
      localStorage.removeItem(LANGUAGE_SELECTED_KEY)
      setLanguageSelected(false)
    }
    window.addEventListener('open-language-modal', reopen)
    return () => window.removeEventListener('open-language-modal', reopen)
  }, [])

  // R50C-1: No body scroll lock — hero is always visible on first load.

  return (
    <Suspense fallback={null}>
    <GatedEntry>
      <AuthProvider>
        <AppReadyProvider>
          <ThemeProvider>
            <HeaderPrefsProvider>
              <LayoutProvider>
                <CurrencyProvider>
                  <AudioPlayerProvider>
                    <EphemeralTokenProvider>
                      <UniversalChatProvider>
                      <AppContent languageSelected={languageSelected} />
                      {/* Dev-only: reset language experience + cookies */}
                      {import.meta.env.DEV && languageSelected && (
                        <button
                          onClick={() => {
                            localStorage.removeItem(LANGUAGE_SELECTED_KEY)
                            localStorage.removeItem('lukhas_language')
                            localStorage.removeItem('lukhas_consent_v1')
                            window.location.reload()
                          }}
                          className="fixed top-4 left-4 z-[999999] px-3 py-2 text-xs font-bold text-white bg-red-600/80 rounded hover:bg-red-500 transition-colors backdrop-blur-sm"
                        >
                          Reset Experience
                        </button>
                      )}
                    </UniversalChatProvider>
                    </EphemeralTokenProvider>
                  </AudioPlayerProvider>
                </CurrencyProvider>
              </LayoutProvider>
            </HeaderPrefsProvider>
          </ThemeProvider>
        </AppReadyProvider>
      </AuthProvider>
    </GatedEntry>
    </Suspense>
  )
}


function App() {
  const location = useLocation()

  // The public root stays concise while the full website and canonical
  // production identity are completed. All other routes keep their
  // existing access controls and are not exposed by this release.
  if (location.pathname === '/') {
    return (
      <Suspense fallback={null}>
        <HoldingPage />
      </Suspense>
    )
  }

  return <GatedApplication />
}

export default App
